import clsx from 'clsx'
import Image from 'next/image'
import NextLink from 'next/link'
import { useMemo, type FC } from 'react'

import type { TextProps } from '@wartek-id/text-v1'
import { Text } from '@wartek-id/text-v1'
import type { ButtonProps } from '@wartek-id/button-v1'
import { Button } from '@wartek-id/button-v1'
import type { BadgeProps } from '@wartek-id/badge'
import { Badge } from '@wartek-id/badge'

import { Card } from 'app/KinerjaShared/components/Card'
import CardStateLoading from 'app/KinerjaShared/components/CardStateLoading'
import { PENGELOLAAN_KINERJA_KADIN_PATH } from 'app/KinerjaShared/constants/path'
import usePenetapanTahunanCountDinas from 'app/KinerjaDinasPage/hooks/usePenetapanTahunanCountDinas'
import usePeriodContext from 'app/KinerjaShared/hooks/usePeriodContext'

type CardPenetapanTahunanProps = {}

const CardPenetapanTahunan: FC<CardPenetapanTahunanProps> = ({}) => {
  const { periodYear } = usePeriodContext()

  const {
    // Common
    isLoading,
    isWithinAllowedRange,
    isClickable,

    // Combined
    countSent,
    isAllSent,
  } = usePenetapanTahunanCountDinas()

  const data: {
    // Copywriting-related
    text?: TextProps & { children?: React.ReactNode | string }
    badge?: BadgeProps & { children?: React.ReactNode | string }
    button?: ButtonProps & { children?: React.ReactNode | string }
  } = useMemo(() => {
    if (isClickable) {
      if (isAllSent) {
        return {
          badge: { variant: 'success', children: 'Sudah selesai ditetapkan' },
          button: { color: 'white', children: 'Cek Penetapan' },
        }
      }

      if (countSent >= 1) {
        return {
          badge: {
            variant: 'informational',
            children: `${countSent} sudah ditetapkan`,
          },
          button: { color: 'blue', children: 'Lakukan Penetapan' },
        }
      }

      return {
        text: {
          children:
            'Lakukan sesuai rekomendasi sistem atau ubah sesuai kebutuhan',
        },
        button: { color: 'blue', children: 'Lakukan Penetapan' },
      }
    }

    return {
      text: {
        children:
          'Lakukan sesuai rekomendasi sistem atau ubah sesuai kebutuhan',
      },
    }
  }, [countSent, isAllSent, isClickable])

  if (isLoading) return <CardStateLoading />
  if (!isWithinAllowedRange) return null

  return (
    <Card
      className={clsx(
        // Handle container
        '!rounded !m-0',
        'bg-white',

        // Handle content
        '!flex !flex-row items-center gap-6 p-6 relative overflow-hidden'
      )}
    >
      <div className="flex-none">
        <Image
          alt=""
          src="/illustrations/img-assessment-subject-book.png"
          width={64}
          height={64}
          className="select-none pointer-events-none"
        />
      </div>

      <div className="flex-1">
        <div className="flex flex-col gap-1 justify-center">
          <>
            <Text variant="heading-lg">
              Penetapan Predikat Kinerja (Tahunan - {periodYear})
            </Text>

            <div>
              {data?.text?.children && (
                <Text {...data.text}>{data.text.children}</Text>
              )}

              {data?.badge?.children && (
                <Badge {...data?.badge}>{data.badge.children}</Badge>
              )}
            </div>
          </>
        </div>
      </div>

      <div className="flex-none z-10">
        {isClickable && data?.button?.children ? (
          <NextLink
            href={`${PENGELOLAAN_KINERJA_KADIN_PATH}/penetapan-tahunan`}
            passHref
          >
            <Button
              as="a"
              color="white"
              size="lg"
              className="w-52"
              {...data.button}
            >
              {data.button.children}
            </Button>
          </NextLink>
        ) : (
          <Badge variant="informational">
            Lakukan penetapan periode Juli-Desember terlebih dahulu
          </Badge>
        )}
      </div>
    </Card>
  )
}

export default CardPenetapanTahunan
